import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import CompanyFormModal, { CompanyRow } from "./CompanyFormModal";

type Lang = "vi" | "kr";

interface Props {
  lang: Lang;
}

const CompaniesManager: React.FC<Props> = ({ lang }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editCompany, setEditCompany] = useState<CompanyRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CompanyRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState("");

  const fetchCompanies = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("companies")
      .select("*")
      .order("created_at", { ascending: false });
    setCompanies((data as CompanyRow[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await supabase.from("companies").delete().eq("id", deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    showToast(t("Đã xóa doanh nghiệp!", "기업이 삭제되었습니다!"));
    fetchCompanies();
  };

  const filtered = companies.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.name_kr ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (c.location ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {toast && (
        <div className="fixed top-4 right-4 z-[60] bg-green-600 text-white text-sm px-4 py-2.5 rounded-xl">
          <i className="ri-checkbox-circle-line mr-2" />{toast}
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">{t("Quản Lý Doanh Nghiệp", "기업 관리")}</h1>
        <p className="text-sm text-gray-500 mt-1">{t("Thêm, sửa, xóa thông tin và logo doanh nghiệp", "기업 정보 및 로고 추가·수정·삭제")}</p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-80">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Tìm theo tên công ty...", "회사명으로 검색...")}
            className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-[#004A99] outline-none w-full"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1.5 rounded-lg whitespace-nowrap">
            {filtered.length} {t("doanh nghiệp", "개 기업")}
          </span>
          <button
            onClick={() => { setEditCompany(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            {t("Thêm doanh nghiệp", "기업 추가")}
          </button>
        </div>
      </div>

      {/* Company Grid */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-200 py-12 text-center">
          <i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin block mb-2" />
          <p className="text-sm text-gray-400">{t("Đang tải...", "로딩 중...")}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
          <i className="ri-building-line text-3xl block mb-2" />
          {t("Chưa có doanh nghiệp nào. Hãy thêm doanh nghiệp đầu tiên!", "아직 기업이 없습니다. 첫 번째 기업을 추가해 보세요!")}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((company) => (
            <div key={company.id} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-[#004A99]/30 transition-colors">
              <div className="flex items-start gap-4">
                {/* Logo */}
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ background: company.logo_url ? undefined : `linear-gradient(135deg, ${company.logo_from ?? "#1C4E6B"}, ${company.logo_to ?? "#2B7A9C"})` }}
                >
                  {company.logo_url ? (
                    <img src={company.logo_url} alt={company.name} className="w-full h-full object-contain rounded-xl p-1" style={{ maxHeight: 56 }} />
                  ) : (
                    <span>{company.logo_initials ?? "CO"}</span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 text-sm truncate">{company.name}</p>
                  {company.name_kr && (
                    <p className="text-[11px] text-gray-400 truncate">{company.name_kr}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {company.location && (
                      <span className="flex items-center gap-1 text-[10px] text-gray-500">
                        <i className="ri-map-pin-line" />{company.location}
                      </span>
                    )}
                    {company.founded && (
                      <span className="flex items-center gap-1 text-[10px] text-gray-500">
                        <i className="ri-calendar-line" />{t("Thành lập", "설립")} {company.founded}
                      </span>
                    )}
                    {company.employees && (
                      <span className="flex items-center gap-1 text-[10px] text-gray-500">
                        <i className="ri-team-line" />{company.employees}
                      </span>
                    )}
                  </div>
                  {company.main_category_name && (
                    <span className="inline-block mt-2 text-[10px] px-2 py-0.5 bg-[#EDF2FB] text-[#004A99] rounded-full font-semibold whitespace-nowrap">
                      {company.main_category_name}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => { setEditCompany(company); setShowModal(true); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-green-50 text-green-600 transition-colors cursor-pointer"
                    title={t("Chỉnh sửa", "수정")}
                  >
                    <i className="ri-edit-line text-sm" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(company)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 transition-colors cursor-pointer"
                    title={t("Xóa", "삭제")}
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>

              {/* Tagline */}
              {(company.tagline_vi || company.tagline_kr) && (
                <p className="text-[11px] text-gray-500 mt-3 pl-[4.5rem] italic line-clamp-2">
                  "{lang === "vi" ? company.tagline_vi : (company.tagline_kr ?? company.tagline_vi)}"
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Company Form Modal */}
      {showModal && (
        <CompanyFormModal
          lang={lang}
          editData={editCompany}
          onClose={() => { setShowModal(false); setEditCompany(null); }}
          onSuccess={() => {
            setShowModal(false);
            setEditCompany(null);
            showToast(editCompany ? t("Đã cập nhật doanh nghiệp!", "기업이 수정되었습니다!") : t("Đã thêm doanh nghiệp mới!", "새 기업이 추가되었습니다!"));
            fetchCompanies();
          }}
        />
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-delete-bin-line text-red-600 text-xl" />
            </div>
            <h3 className="font-bold text-gray-800 mb-2">{t("Xác nhận xóa", "삭제 확인")}</h3>
            <p className="text-sm text-gray-600 mb-5">
              {t("Bạn có chắc muốn xóa doanh nghiệp", "다음 기업을 삭제하시겠습니까?")}
              {" "}<strong>"{deleteTarget.name}"</strong>?{" "}
              {t("Hành động này không thể hoàn tác.", "이 작업은 취소할 수 없습니다.")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                {t("Hủy", "취소")}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {deleting && <i className="ri-loader-4-line animate-spin" />}
                {t("Xóa", "삭제")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesManager;
